<template>
  <section
    id="check-appointment"
    class="reference-lookup-section"
  >
    <div class="section-container">
      <div class="row align-items-center g-4">
        <!-- Left: Lookup Form -->
        <div class="col-12 col-lg-6">
          <span class="section-badge outline">
            Track Status
          </span>
          <h2 class="section-title mt-2">
            Check Your Appointment
          </h2>
          <p class="section-subtitle">
            Enter your reference number to check the status of your appointment.
          </p>

          <div class="lookup-card bg-white rounded-4 p-4 shadow-sm mt-4">
            <form
              novalidate
              @submit.prevent="onLookup"
            >
              <label
                for="refNumber"
                class="form-label fw-bold small"
                style="color: #475569;"
              >
                Reference Number
              </label>
              <div class="input-group mb-3">
                <span
                  class="input-group-text"
                  style="background: #f8fafc; border-color: #e2e8f0; color: #94a3b8;"
                >
                  <i class="fas fa-search" />
                </span>
                <input
                  id="refNumber"
                  v-model="referenceNumber"
                  type="text"
                  class="form-control"
                  placeholder="e.g., APT-20240001"
                  :disabled="loading"
                  style="border-color: #e2e8f0;"
                >
                <button
                  type="submit"
                  class="btn"
                  :disabled="loading || !referenceNumber.trim()"
                  style="background: linear-gradient(135deg, #10b981, #059669); color: white; border: none;"
                >
                  <i :class="['fas', loading ? 'fa-spinner fa-spin' : 'fa-arrow-right']" />
                </button>
              </div>
            </form>

            <div
              v-if="errorMessage"
              class="alert alert-danger py-2 small"
            >
              <i class="fas fa-exclamation-circle me-1" />
              {{ errorMessage }}
            </div>

            <!-- Appointment Result -->
            <div
              v-if="appointment"
              class="appointment-result mt-3 p-3 rounded-3"
              style="background: #f0fdf4; border: 1px solid #bbf7d0;"
            >
              <div class="d-flex justify-content-between align-items-start mb-2">
                <h6
                  class="fw-bold mb-0"
                  style="color: #166534;"
                >
                  <i class="fas fa-check-circle me-1" />
                  Appointment Found
                </h6>
                <span
                  class="badge"
                  :class="statusBadgeClass"
                >
                  {{ statusLabel }}
                </span>
              </div>

              <div class="row g-2 small">
                <div
                  v-if="appointment.reference_number"
                  class="col-6"
                >
                  <strong style="color: #475569;">Reference:</strong>
                  <div style="color: #1e293b;">
                    {{ appointment.reference_number }}
                  </div>
                </div>
                <div
                  v-if="appointment.appointment_slot"
                  class="col-6"
                >
                  <strong style="color: #475569;">Schedule:</strong>
                  <div style="color: #1e293b;">
                    {{ appointment.appointment_slot.date }}
                    <br>{{ appointment.appointment_slot.start_time }} - {{ appointment.appointment_slot.end_time }}
                  </div>
                </div>
                <div
                  v-if="appointment.appointment_concern_type"
                  class="col-6"
                >
                  <strong style="color: #475569;">Concern:</strong>
                  <div style="color: #1e293b;">
                    {{ appointment.appointment_concern_type.name }}
                  </div>
                </div>
                <div
                  v-if="appointment.full_name"
                  class="col-6"
                >
                  <strong style="color: #475569;">Name:</strong>
                  <div style="color: #1e293b;">
                    {{ appointment.full_name }}
                  </div>
                </div>
                <div
                  v-if="appointment.description"
                  class="col-12"
                >
                  <strong style="color: #475569;">Description:</strong>
                  <div style="color: #1e293b;">
                    {{ appointment.description }}
                  </div>
                </div>
              </div>

              <div
                v-if="canCancelAppointment"
                class="mt-3"
              >
                <button
                  class="btn btn-sm btn-outline-danger"
                  :disabled="loading"
                  @click="onCancelAppointment"
                >
                  <i class="fas fa-times-circle me-1" />
                  Cancel Appointment
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Info -->
        <div class="col-12 col-lg-6 d-flex flex-column align-items-center">
          <div class="text-center">
            <div
              class="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
              style="width: 100px; height: 100px; background: linear-gradient(135deg, rgba(16,185,129,0.1), rgba(5,150,105,0.1)); color: #059669;"
            >
              <i
                class="fas fa-clipboard-check"
                style="font-size: 2.5rem;"
              />
            </div>
            <h5
              class="fw-bold mb-3"
              style="color: #1e293b;"
            >
              How It Works
            </h5>
          </div>
          <div
            class="d-flex flex-column gap-3 w-100"
            style="max-width: 380px;"
          >
            <div class="d-flex align-items-center gap-3">
              <div
                class="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style="width: 36px; height: 36px; background: linear-gradient(135deg, #10b981, #059669); color: #fff; font-size: 0.85rem; font-weight: 700;"
              >
                1
              </div>
              <p
                class="mb-0 small"
                style="color: #475569;"
              >
                Enter the reference number you received when you booked your appointment
              </p>
            </div>
            <div class="d-flex align-items-center gap-3">
              <div
                class="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style="width: 36px; height: 36px; background: linear-gradient(135deg, #10b981, #059669); color: #fff; font-size: 0.85rem; font-weight: 700;"
              >
                2
              </div>
              <p
                class="mb-0 small"
                style="color: #475569;"
              >
                Click search to retrieve your appointment details and current status
              </p>
            </div>
            <div class="d-flex align-items-center gap-3">
              <div
                class="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style="width: 36px; height: 36px; background: linear-gradient(135deg, #10b981, #059669); color: #fff; font-size: 0.85rem; font-weight: 700;"
              >
                3
              </div>
              <p
                class="mb-0 small"
                style="color: #475569;"
              >
                View your appointment schedule, concern type, and approval status
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import '@/assets/css/landing/sections/common.css'
import LandingService from '@/services/shared/landing.service'

export default {
  name: 'ReferenceLookupSection',

  data () {
    return {
      referenceNumber: '',
      loading: false,
      errorMessage: '',
      appointment: null
    }
  },

  computed: {
    statusBadgeClass () {
      if (!this.appointment) return ''
      switch (String(this.appointment.status || '').toLowerCase()) {
        case 'scheduled': return 'bg-success'
        case 'for_confirmation': return 'bg-warning text-dark'
        case 'canceled': return 'bg-danger'
        case 'completed': return 'bg-info'
        default: return 'bg-secondary'
      }
    },

    statusLabel () {
      if (!this.appointment || !this.appointment.status) return 'Unknown'

      const map = {
        for_confirmation: 'For Confirmation',
        scheduled: 'Scheduled',
        canceled: 'Canceled',
        completed: 'Completed'
      }

      const key = String(this.appointment.status).toLowerCase()
      return map[key] || this.appointment.status
    },

    canCancelAppointment () {
      if (!this.appointment || !this.appointment.status) return false
      const status = String(this.appointment.status).toLowerCase()
      return status === 'for_confirmation' || status === 'scheduled'
    }
  },

  methods: {
    async onLookup () {
      const ref = this.referenceNumber.trim()
      if (!ref) return

      this.loading = true
      this.errorMessage = ''
      this.appointment = null

      try {
        const { data } = await LandingService.checkAppointment(ref)
        if (data.success && data.data) {
          this.appointment = data.data
        } else {
          this.errorMessage = data.message || 'Appointment not found.'
        }
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message?.message || 'Appointment not found. Please check your reference number.'
        this.errorMessage = msg
      } finally {
        this.loading = false
      }
    },

    async onCancelAppointment () {
      const ref = this.referenceNumber.trim()
      if (!ref || !this.canCancelAppointment) return

      this.loading = true
      this.errorMessage = ''

      try {
        const { data } = await LandingService.cancelAppointment(ref)

        this.$bvToast.toast(data?.message || 'Appointment canceled successfully.', {
          title: 'Canceled',
          variant: 'success',
          solid: true
        })

        await this.onLookup()
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message?.message || 'Failed to cancel appointment.'
        this.errorMessage = msg
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.reference-lookup-section {
  padding: clamp(2rem, 5vw, 4rem) 0;
  background: #f8fafc;
}

.lookup-card {
  border: 1px solid #e2e8f0;
}

.lookup-card .form-control:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 0.2rem rgba(16, 185, 129, 0.15);
}
</style>
